// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Trucks CRUD
// Truck inventory, certification status, capacity
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Switch,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, Tooltip, FileInput, Image,
} from '../../components/gab/GabComponents';
import { DateInput } from '../../components/gab/MantineDates';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconCertificate, IconPhoto,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debrisStore';
import { useAuthStore } from '../../store/authStore';
import type { DebrisTruck } from '../../types/debris';

const STATUS_OPTIONS = [
  { value: 'certified', label: 'Certified' },
  { value: 'pending', label: 'Pending' },
  { value: 'decertified', label: 'Decertified' },
];

const CERT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'certified', label: 'Certified' },
  { value: 'expired', label: 'Expired' },
];

const TRUCK_TYPES = [
  { value: 'trailer', label: 'Trailer' },
  { value: 'dump_truck', label: 'Dump Truck' },
  { value: 'knuckleboom', label: 'Knuckleboom' },
  { value: 'roll_off', label: 'Roll-Off' },
  { value: 'self_loader', label: 'Self-Loader' },
];

const STATUS_COLORS: Record<string, string> = {
  certified: 'green', pending: 'yellow', decertified: 'red',
};

const CERT_STATUS_COLORS: Record<string, string> = {
  certified: 'green', pending: 'yellow', expired: 'red',
};

const EMPTY: Partial<DebrisTruck> = {
  contractorId: '', truckNumber: '', licensePlate: '', truckType: '',
  lengthFt: '', widthFt: '', heightFt: '', cubicYardCapacity: '',
  certifiedDate: '', certifiedByName: '', status: 'pending', notes: '', hasTailgate: 'true',
  certExpiresAt: '', certStatus: 'pending', placardNumber: '', bedPhotoUrl: '',
};

/** Compute effective cert status taking expiration into account */
function getEffectiveCertStatus(truck: DebrisTruck): { label: string; color: string } {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const expires = truck.certExpiresAt ? new Date(truck.certExpiresAt) : null;

  if (expires && expires.getTime() < now.getTime()) {
    return { label: 'Expired', color: 'red' };
  }

  const sevenDays = new Date(now);
  sevenDays.setDate(sevenDays.getDate() + 7);
  if (expires && expires.getTime() <= sevenDays.getTime() && (truck.certStatus === 'certified')) {
    return { label: 'Expiring Soon', color: 'yellow' };
  }

  const status = truck.certStatus || 'pending';
  return { label: status.charAt(0).toUpperCase() + status.slice(1), color: CERT_STATUS_COLORS[status] ?? 'gray' };
}

/** Convert a File to a base64 data URL string */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Format date string as YYYY-MM-DD */
function fmtDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function Trucks() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    trucks, contractors, loadingTrucks,
    loadTrucks, loadContractors,
    createTruck, updateTruck, deleteTruck,
  } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [contractorFilter, setContractorFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisTruck | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisTruck | null>(null);
  const [form, setForm] = useState<Partial<DebrisTruck>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [manualCapacity, setManualCapacity] = useState(false);
  const [certifyMode, setCertifyMode] = useState(false);
  const certSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => { loadTrucks(); loadContractors(); }, [loadTrucks, loadContractors]);

  const iTrucks = incidentId ? trucks : [];
  const iContractors = incidentId ? contractors : [];

  const contractorSelect = useMemo(() =>
    iContractors.map(c => ({ value: String(c.id), label: c.companyName || `#${c.id}` })),
  [iContractors]);

  const filtered = useMemo(() => {
    let list = iTrucks;
    if (statusFilter && statusFilter !== 'all') list = list.filter(t => t.status === statusFilter);
    if (contractorFilter && contractorFilter !== 'all') list = list.filter(t => t.contractorId === contractorFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        (t.truckNumber || '').toLowerCase().includes(q) ||
        (t.licensePlate || '').toLowerCase().includes(q) ||
        (t.placardNumber || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (a.truckNumber || '').localeCompare(b.truckNumber || ''));
  }, [iTrucks, statusFilter, contractorFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [statusFilter, contractorFilter, search]);

  // --- Cert stats ---
  const certStats = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const sevenDays = new Date(now);
    sevenDays.setDate(sevenDays.getDate() + 7);

    let certified = 0;
    let pending = 0;
    let expiredOrExpiring = 0;

    for (const t of iTrucks) {
      const expires = t.certExpiresAt ? new Date(t.certExpiresAt) : null;
      if (expires && expires.getTime() < now.getTime()) {
        expiredOrExpiring++;
      } else if (expires && expires.getTime() <= sevenDays.getTime() && t.certStatus === 'certified') {
        expiredOrExpiring++;
      } else if (t.certStatus === 'certified') {
        certified++;
      } else {
        pending++;
      }
    }
    return { certified, pending, expiredOrExpiring };
  }, [iTrucks]);

  const contractorName = (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? id;
  const setField = (key: keyof DebrisTruck, value: string) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setManualCapacity(false); setCertifyMode(false); setModalOpen(true); };
  const openEdit = (t: DebrisTruck) => { setEditTarget(t); setForm({ ...t }); setManualCapacity(!!t.cubicYardCapacity); setCertifyMode(false); setModalOpen(true); };

  const openCertify = useCallback((t: DebrisTruck) => {
    const today = fmtDate(new Date());
    const defaultExpires = new Date();
    defaultExpires.setDate(defaultExpires.getDate() + 30);

    setEditTarget(t);
    setForm({
      ...t,
      certifiedDate: today,
      certStatus: 'certified',
      certExpiresAt: t.certExpiresAt || fmtDate(defaultExpires),
    });
    setManualCapacity(!!t.cubicYardCapacity);
    setCertifyMode(true);
    setModalOpen(true);
    // Scroll to cert section after modal renders
    setTimeout(() => {
      certSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  }, []);

  // Auto-calculate capacity from dimensions
  const calcCapacity = (l: string, w: string, h: string) => {
    const lf = parseFloat(l) || 0;
    const wf = parseFloat(w) || 0;
    const hf = parseFloat(h) || 0;
    if (lf && wf && hf) return ((lf * wf * hf) / 27).toFixed(1);
    return '';
  };

  const handleDimensionChange = (key: 'lengthFt' | 'widthFt' | 'heightFt', value: string) => {
    const next = { ...form, [key]: value };
    const cap = calcCapacity(next.lengthFt ?? '', next.widthFt ?? '', next.heightFt ?? '');
    if (!manualCapacity && cap) {
      setForm({ ...next, cubicYardCapacity: cap });
    } else {
      setForm(next);
    }
  };

  const handleBedPhoto = async (file: File | null) => {
    if (!file) {
      setField('bedPhotoUrl', '');
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setField('bedPhotoUrl', base64);
    } catch {
      notifications.show({ message: 'Failed to read photo', color: 'red' });
    }
  };

  const handleSave = async () => {
    if (!form.truckNumber?.trim()) {
      notifications.show({ message: 'Truck number is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, incidentId };
      if (editTarget) {
        await updateTruck(editTarget.id, payload);
        notifications.show({ message: 'Truck updated', color: 'green' });
      } else {
        await createTruck(payload);
        notifications.show({ message: 'Truck created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteTruck(deleteTarget.id);
      notifications.show({ message: `Truck ${deleteTarget.truckNumber} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Trucks</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingTrucks} onClick={() => loadTrucks()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Truck</Button>
        </Group>
      </Group>

      {/* Cert stats */}
      {iTrucks.length > 0 && (
        <SimpleGrid cols={{ base: 3, sm: 3 }} mb="md" spacing="sm">
          <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--mantine-color-green-6)' }}>
            <Text size="xl" fw={700} c="green">{certStats.certified}</Text>
            <Text size="xs" c="dimmed">Certified</Text>
          </Paper>
          <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--mantine-color-yellow-6)' }}>
            <Text size="xl" fw={700} c="yellow.7">{certStats.pending}</Text>
            <Text size="xs" c="dimmed">Pending</Text>
          </Paper>
          <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--mantine-color-red-6)' }}>
            <Text size="xl" fw={700} c="red">{certStats.expiredOrExpiring}</Text>
            <Text size="xs" c="dimmed">Expired / Expiring</Text>
          </Paper>
        </SimpleGrid>
      )}

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search truck #, plate, placard..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'} onChange={v => setStatusFilter(v)} size="xs" w={160} />
        <Select data={[{ value: 'all', label: 'All Contractors' }, ...contractorSelect]}
          value={contractorFilter ?? 'all'} onChange={v => setContractorFilter(v)} size="xs" w={200}
          searchable />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} truck{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Truck #</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Placard #</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Plate</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Contractor</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>L x W x H</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>CY Cap</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Cert Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Certified</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingTrucks ? 'Loading...' : 'No trucks found'}
                </td></tr>
              )}
              {pageRows.map(t => {
                const ecs = getEffectiveCertStatus(t);
                return (
                  <tr key={t.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', cursor: 'pointer' }}
                    onClick={() => openEdit(t)}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{t.truckNumber || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{t.placardNumber || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>{t.licensePlate || '-'}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {TRUCK_TYPES.find(tt => tt.value === t.truckType)?.label ?? t.truckType ?? '-'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{t.contractorId ? contractorName(t.contractorId) : '-'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: '0.8rem' }}>
                      {t.lengthFt && t.widthFt && t.heightFt ? `${t.lengthFt}×${t.widthFt}×${t.heightFt}` : '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                      {t.cubicYardCapacity ? `${parseFloat(t.cubicYardCapacity).toFixed(1)}` : '-'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="sm" color={STATUS_COLORS[t.status] ?? 'gray'}>{t.status}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="sm" color={ecs.color}>{ecs.label}</Badge>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Text size="xs">{t.certifiedDate || '-'}</Text>
                      <Text size="xs" c="dimmed">{t.certifiedByName}</Text>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <Group gap={4} justify="flex-end">
                        <Tooltip label="Certify" withArrow>
                          <ActionIcon size="sm" variant="subtle" color="green" onClick={() => openCertify(t)}>
                            <IconCertificate size={14} />
                          </ActionIcon>
                        </Tooltip>
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(t)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(t)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="blue" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => { setModalOpen(false); setCertifyMode(false); }}
        title={certifyMode ? `Certify Truck ${editTarget?.truckNumber ?? ''}` : editTarget ? 'Edit Truck' : 'Add Truck'}
        centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Truck Number" required value={form.truckNumber ?? ''}
              onChange={e => setField('truckNumber', e.target.value)} />
            <TextInput label="License Plate" value={form.licensePlate ?? ''}
              onChange={e => setField('licensePlate', e.target.value)} />
            <Select label="Truck Type" data={TRUCK_TYPES} value={form.truckType || null}
              onChange={v => setField('truckType', v ?? '')} clearable />
            <Select label="Contractor" data={contractorSelect} value={form.contractorId || null}
              onChange={v => setField('contractorId', v ?? '')} searchable clearable />
          </SimpleGrid>

          <Text size="sm" fw={600} c="dimmed">Dimensions (feet)</Text>
          <SimpleGrid cols={4} spacing="sm">
            <TextInput label="Length" value={form.lengthFt ?? ''}
              onChange={e => handleDimensionChange('lengthFt', e.target.value)} />
            <TextInput label="Width" value={form.widthFt ?? ''}
              onChange={e => handleDimensionChange('widthFt', e.target.value)} />
            <TextInput label="Height" value={form.heightFt ?? ''}
              onChange={e => handleDimensionChange('heightFt', e.target.value)} />
            <TextInput label="CY Capacity" value={form.cubicYardCapacity ?? ''}
              onChange={e => { setManualCapacity(true); setField('cubicYardCapacity', e.target.value); }}
              description={manualCapacity ? 'Manual' : 'Auto-calculated'} />
          </SimpleGrid>

          <div ref={certSectionRef}>
            <Text size="sm" fw={600} c="dimmed" mb="xs">Certification & FEMA Compliance</Text>
          </div>
          {certifyMode && (
            <Paper withBorder p="xs" bg="green.0" mb="xs">
              <Text size="xs" c="green.8" fw={600}>
                Certify mode: certified date set to today, status set to certified. Confirm expiration date and save.
              </Text>
            </Paper>
          )}
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <Select label="Certification Status" data={CERT_STATUS_OPTIONS}
              value={form.certStatus || 'pending'}
              onChange={v => { setField('certStatus', v ?? 'pending'); setField('status', v ?? 'pending'); }} />
            <DateInput label="Certified Date" clearable valueFormat="YYYY-MM-DD"
              value={form.certifiedDate ? new Date(form.certifiedDate) : null}
              onChange={(d: any) => setField('certifiedDate', d ? fmtDate(new Date(d)) : '')} />
            <TextInput label="Certified By" value={form.certifiedByName ?? ''}
              onChange={e => setField('certifiedByName', e.target.value)} />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
            <DateInput label="Cert Expires" clearable valueFormat="YYYY-MM-DD"
              value={form.certExpiresAt ? new Date(form.certExpiresAt) : null}
              onChange={(d: any) => setField('certExpiresAt', d ? fmtDate(new Date(d)) : '')} />
            <TextInput label="Placard Number" value={form.placardNumber ?? ''}
              onChange={e => setField('placardNumber', e.target.value)}
              description="Physical placard ID on the truck" />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <FileInput
              label="Bed Photo"
              description="Photo of the measured truck bed"
              placeholder="Select image..."
              accept="image/*"
              leftSection={<IconPhoto size={14} />}
              onChange={handleBedPhoto}
              clearable
            />
            {form.bedPhotoUrl && (
              <div>
                <Text size="xs" c="dimmed" mb={4}>Preview</Text>
                <Image src={form.bedPhotoUrl} alt="Bed photo" maw={200} mah={150} radius="sm" fit="contain" />
              </div>
            )}
          </SimpleGrid>

          <Switch
            label="Has structural tailgate"
            description="No tailgate = automatic 15% FEMA capacity reduction (max 85%)"
            checked={form.hasTailgate !== 'false'}
            onChange={e => setField('hasTailgate', e.currentTarget.checked ? 'true' : 'false')}
          />

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setModalOpen(false); setCertifyMode(false); }}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {certifyMode ? 'Save Certification' : editTarget ? 'Save Changes' : 'Create Truck'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Truck" centered size="sm">
        <Stack>
          <Text size="sm">Delete truck <strong>{deleteTarget?.truckNumber}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
